/**
 * F16: pure slug validation rules for `tenantBranding.clientSlug`. Exported
 * separately from the mutation so vitest can exercise the rules without
 * loading the Convex runtime.
 *
 * Format: lowercase letters, digits, hyphens. Length 2-40. No leading or
 * trailing hyphen. No consecutive hyphens.
 *
 * Reserved values block routes that the SPA also serves (admin tooling,
 * platform endpoints) and short host-like names that would collide with
 * future top-level routes.
 */

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // Platform / framework
  "admin",
  "api",
  "auth",
  "static",
  "assets",
  "public",
  "c", // would collide with /c itself
  // Reserve short prefixes for future top-level routes
  "trust",
  "settings",
  "demo",
  "signin",
  "signout",
  "signed-out",
  "login",
  "logout",
  "callback",
  // Repancy-internal
  "repancy",
  "arewe",
]);

const SLUG_RE = /^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$/;

export type SlugValidationError =
  | "EmptySlug"
  | "TooShort"
  | "TooLong"
  | "ReservedSlug"
  | "InvalidFormat";

/**
 * Throws a tagged `Error` with `name = SlugValidationError` on failure.
 * Returns the normalised slug (lowercased) on success.
 */
export function validateSlug(input: string): string {
  if (typeof input !== "string") {
    const err = new Error("EmptySlug: clientSlug is required");
    err.name = "EmptySlug";
    throw err;
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    const err = new Error("EmptySlug: clientSlug is required");
    err.name = "EmptySlug";
    throw err;
  }
  const normalised = trimmed.toLowerCase();
  if (normalised.length < 2) {
    const err = new Error(`TooShort: slug "${normalised}" must be at least 2 chars`);
    err.name = "TooShort";
    throw err;
  }
  if (normalised.length > 40) {
    const err = new Error(`TooLong: slug must be at most 40 chars`);
    err.name = "TooLong";
    throw err;
  }
  if (RESERVED_SLUGS.has(normalised) || normalised.startsWith("_")) {
    const err = new Error(`ReservedSlug: "${normalised}" is reserved`);
    err.name = "ReservedSlug";
    throw err;
  }
  if (!SLUG_RE.test(normalised)) {
    const err = new Error(
      `InvalidFormat: slug must match /^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$/`,
    );
    err.name = "InvalidFormat";
    throw err;
  }
  return normalised;
}

/**
 * Pure predicate version for callers that prefer a boolean check
 * (e.g. SPA form validation before submit).
 */
export function isValidSlug(input: string): boolean {
  try {
    validateSlug(input);
    return true;
  } catch {
    return false;
  }
}

const PRIMARY_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Throws on invalid hex. Returns the value unchanged on success.
 * Accepts 3- or 6-digit hex with leading #. Case-insensitive.
 */
export function validatePrimaryColor(input: string): string {
  if (typeof input !== "string" || !PRIMARY_COLOR_RE.test(input)) {
    const err = new Error(
      `InvalidPrimaryColor: must be #RGB or #RRGGBB hex, got "${input}"`,
    );
    err.name = "InvalidPrimaryColor";
    throw err;
  }
  return input;
}

const TENANT_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Entra tenant IDs are GUIDs. Reject anything else. */
export function validateTenantId(input: string): string {
  if (typeof input !== "string" || !TENANT_ID_RE.test(input)) {
    const err = new Error(
      `InvalidTenantId: must be a GUID, got "${input}"`,
    );
    err.name = "InvalidTenantId";
    throw err;
  }
  return input.toLowerCase();
}
